import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const institutionId = searchParams.get("institution_id");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the session after OAuth redirect
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error("Auth callback error:", error);
          navigate("/auth?error=callback_failed");
          return;
        }

        if (session?.user && institutionId) {
          // Link the user to the institution
          const { error: updateError } = await supabase
            .from("profiles")
            .update({ institution_id: institutionId })
            .eq("id", session.user.id);

          if (updateError) {
            console.error("Error linking institution:", updateError);
          } else {
            // Defensive: Ensure subscription has the free session for institutional users
            // This handles edge cases where the trigger might not fire correctly
            const { error: subscriptionError } = await supabase
              .from("subscriptions")
              .update({ interview_sessions_remaining: 1 })
              .eq("user_id", session.user.id)
              .eq("tier", "free")
              .eq("interview_sessions_remaining", 0);

            if (subscriptionError) {
              console.error("Error updating subscription for institutional user:", subscriptionError);
            }
          }
        }

        // Check if user is an institution admin (TPO)
        if (session?.user) {
          const { data: institutionAdminRows, error: institutionAdminError } = await supabase
            .from("institution_admins")
            .select("id")
            .eq("user_id", session.user.id)
            .limit(1);

          if (institutionAdminError) {
            console.error("Error checking institution admin status:", institutionAdminError);
          } else if ((institutionAdminRows?.length ?? 0) > 0) {
            navigate("/institution/dashboard", { replace: true });
            return;
          }
        }

        // Redirect to home dashboard for regular users
        navigate("/home", { replace: true });
      } catch (err) {
        console.error("Callback processing error:", err);
        navigate("/auth?error=callback_failed");
      }
    };

    handleCallback();
  }, [navigate, institutionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
